"use client";

import { parseAsArrayOf, parseAsInteger, parseAsString, useQueryState, useQueryStates } from "nuqs";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { capturePosthogEvent } from "@/lib/posthog-browser";
import { COIN_PAIR_BY_ID } from "../consts/coins";
import type { PoolFilters } from "../store/poolsStore";
import type { CoinPair } from "../types";
import { countActivePoolFilters, networkFilterKey, normalizeNetworkFilter } from "../utils/network-filter";
const URL_STATE_OPTIONS = {
  history: "replace" as const,
  clearOnDefault: true
};
const EMPTY_NETWORKS: string[] = [];
/** Hoisted so `withDefault([])` does not allocate a new default each render. */
const NETWORK_PARSER = parseAsArrayOf(parseAsString).withDefault(EMPTY_NETWORKS);

/**
 * nuqs-backed pools filter state. Use via PoolsFiltersProvider + usePoolsFilters().
 */
export function usePoolsFiltersState() {
  // URL state for filters
  const [urlFilters, setUrlFilters] = useQueryStates({
    keyword: parseAsString.withDefault(""),
    protocol: parseAsString.withDefault("all"),
    network: NETWORK_PARSER,
    feeTier: parseAsString.withDefault("all"),
    tvl: parseAsString.withDefault("all"),
    "1dVolume": parseAsString.withDefault("all"),
    volTvl: parseAsString.withDefault("all"),
    marketCap: parseAsString.withDefault("all")
  }, URL_STATE_OPTIONS);
  const [urlPage, setUrlPage] = useQueryState("page", parseAsInteger.withDefault(1).withOptions(URL_STATE_OPTIONS));

  // URL state for selected coin pair
  const [urlPair, setUrlPair] = useQueryState("pair", parseAsString.withDefault("").withOptions(URL_STATE_OPTIONS));
  const selectedCoinPair: CoinPair | null = urlPair ? COIN_PAIR_BY_ID.get(urlPair) ?? null : null;
  const networkKey = networkFilterKey(urlFilters.network);
  const network = useMemo(() => normalizeNetworkFilter(urlFilters.network), [networkKey]);
  const filters = useMemo<PoolFilters>(() => ({
    keyword: urlFilters.keyword || "",
    protocol: urlFilters.protocol || "all",
    network,
    feeTier: urlFilters.feeTier || "all",
    tvl: urlFilters.tvl || "all",
    "1dVolume": urlFilters["1dVolume"] || "all",
    volTvl: urlFilters.volTvl || "all",
    marketCap: urlFilters.marketCap || "all"
  }), [urlFilters.keyword, urlFilters.protocol, network, urlFilters.feeTier, urlFilters.tvl, urlFilters["1dVolume"], urlFilters.volTvl, urlFilters.marketCap]);
  const urlFiltersRef = useRef(urlFilters);
  urlFiltersRef.current = urlFilters;

  // Debounce ref for keyword updates
  const keywordTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resetPage = useCallback(() => {
    void setUrlPage(null);
  }, [setUrlPage]);

  // Set selected coin pair (converts CoinPair to URL string)
  const setSelectedCoinPair = useCallback((pair: CoinPair | null) => {
    setUrlPair(pair?.id || null, {
      history: "replace"
    });
    resetPage();
    if (pair) {
      void capturePosthogEvent("coin_pair_selected", {
        pair_id: pair.id,
        pair_symbols: `${pair.pairs[0].symbol}/${pair.pairs[1].symbol}`,
        was_deselected: false
      });
    }
  }, [resetPage, setUrlPair]);

  // Set filters with debouncing for keyword
  const setFilters = useCallback((newFilters: PoolFilters, options?: {
    immediate?: boolean;
  }) => {
    const currentFilters = urlFiltersRef.current;
    const isKeywordUpdate = newFilters.keyword !== currentFilters.keyword;

    // Clear existing timeout
    if (keywordTimeoutRef.current) {
      clearTimeout(keywordTimeoutRef.current);
      keywordTimeoutRef.current = null;
    }
    const updateUrl = () => {
      const updates: Partial<typeof urlFilters> = {};

      // Update keyword (set to null if empty to remove from URL)
      if (newFilters.keyword !== currentFilters.keyword) {
        updates.keyword = newFilters.keyword || null as any;
      }

      // Update other filters (set to null if "all" to remove from URL)
      if (newFilters.protocol !== currentFilters.protocol) {
        updates.protocol = newFilters.protocol === "all" ? null as any : newFilters.protocol;
      }
      const nextNetwork = normalizeNetworkFilter(newFilters.network);
      if (networkFilterKey(nextNetwork) !== networkFilterKey(currentFilters.network)) {
        updates.network = nextNetwork.length === 0 ? null as any : nextNetwork;
      }
      if (newFilters.feeTier !== currentFilters.feeTier) {
        updates.feeTier = newFilters.feeTier === "all" ? null as any : newFilters.feeTier;
      }
      if (newFilters.tvl !== currentFilters.tvl) {
        updates.tvl = newFilters.tvl === "all" ? null as any : newFilters.tvl;
      }
      if (newFilters["1dVolume"] !== currentFilters["1dVolume"]) {
        updates["1dVolume"] = newFilters["1dVolume"] === "all" ? null as any : newFilters["1dVolume"];
      }
      if (newFilters.volTvl !== currentFilters.volTvl) {
        updates.volTvl = newFilters.volTvl === "all" ? null as any : newFilters.volTvl;
      }
      if (newFilters.marketCap !== currentFilters.marketCap) {
        updates.marketCap = newFilters.marketCap === "all" ? null as any : newFilters.marketCap;
      }

      // Only update if there are changes
      if (Object.keys(updates).length > 0) {
        setUrlFilters(updates);
        resetPage();
        const changedKey = (Object.keys(updates)[0] ?? "keyword") as keyof PoolFilters;
        const nextFilters = {
          ...filters,
          ...newFilters
        };
        if (changedKey === "keyword" && newFilters.keyword.trim()) {
          void capturePosthogEvent("pool_search_performed", {
            query_length: newFilters.keyword.trim().length
          });
        } else if (changedKey !== "keyword") {
          void capturePosthogEvent("pool_filter_applied", {
            filter_type: changedKey,
            filter_value: String(newFilters[changedKey]),
            has_multiple_filters: countActivePoolFilters(nextFilters, !!selectedCoinPair) > 1
          });
        }
      }
    };

    // Debounce keyword updates, immediate for others
    if (isKeywordUpdate && !options?.immediate) {
      keywordTimeoutRef.current = setTimeout(updateUrl, 500);
    } else {
      updateUrl();
    }
  }, [resetPage, setUrlFilters, filters, selectedCoinPair]);

  // Reset filters to defaults
  const resetFilters = useCallback(() => {
    // Clear keyword timeout if pending
    if (keywordTimeoutRef.current) {
      clearTimeout(keywordTimeoutRef.current);
      keywordTimeoutRef.current = null;
    }

    // Reset all filters and pair to null (removes from URL)
    setUrlFilters({
      keyword: null as any,
      protocol: null as any,
      network: null as any,
      feeTier: null as any,
      tvl: null as any,
      "1dVolume": null as any,
      volTvl: null as any,
      marketCap: null as any
    });
    setUrlPair(null);
    resetPage();
  }, [resetPage, setUrlFilters, setUrlPair]);
  const setPage = useCallback((nextPage: number) => {
    const normalizedPage = Math.max(1, Math.floor(nextPage));
    void setUrlPage(normalizedPage <= 1 ? null : normalizedPage);
  }, [setUrlPage]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (keywordTimeoutRef.current) {
        clearTimeout(keywordTimeoutRef.current);
      }
    };
  }, []);
  return useMemo(() => ({
    filters,
    selectedCoinPair,
    page: Math.max(1, urlPage),
    setFilters,
    setSelectedCoinPair,
    setPage,
    resetFilters
  }), [filters, selectedCoinPair, urlPage, setFilters, setSelectedCoinPair, setPage, resetFilters]);
}